

import { Input, message } from "antd";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import LoadingBox from "../../Components/LoadingBox";
import { API, Headers } from "../../Config";
import PageHeader from "../../Routes/PageHeader";
import DataTable from "./components/dataTable";
import PurchaseForm from "./components/formModal";

const Purchase = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [meta, setMeta] = useState({});
  const [openForm, setOpenForm] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    const params = new URLSearchParams(location.search);
    const page = Number(params.get("page")) || 1;
    const take = Number(params.get("take")) || 10;
    const search = params.get("search") || "";
    setSearchValue(search);
    loadData(page, take, search);
    fetchAccounts();
    return () => { isMounted.current = false; };
  }, [location.search]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(
        `${API.BASR_URL}${API.CHART_OF_ACCOUNTS_LIST}/child_accounts`,
        { headers: Headers() }
      );
      if (isMounted.current && response.status === 200) {
        setAccounts(response.data ?? []);
      }
    } catch (error) {
      if (isMounted.current) {
        message.error("Failed to load accounts");
      }
    }
  };

  const loadData = async (p, t, searchTerm) => {
    setLoading(true);
    try {
      const url = `${API.BASR_URL}${
        API.PURCHASE
      }?order=DESC&page=${p}&take=${t}${
        searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ""
      }`;
      const response = await axios.get(url, { headers: Headers() });
      if (isMounted.current && response.status === 200) {
        setData(response.data?.data ?? []);
        setMeta(response.data?.meta ?? {});
        setExpandedRowKeys(
          searchTerm ? response.data?.data?.map((item) => item?.id) ?? [] : []
        );
      } else if (isMounted.current) {
        message.error("Failed to load purchases");
      }
    } catch (error) {
      if (isMounted.current) {
        setData([]);
        setMeta({});
        setExpandedRowKeys([]);
        message.error("Failed to load purchases");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (item) => {
    try {
      const response = await axios.delete(
        `${API.BASR_URL}${API.PURCHASE}/${item.id}`,
        {
          headers: Headers(),
        }
      );
      if (isMounted.current) {
        if (response.status === 200) {
          message.success("Deleted successfully");
          const params = new URLSearchParams(location.search);
          loadData(Number(params.get("page")) || 1, Number(params.get("take")) || 10, params.get("search") || "");
        } else if (response.status === 400) {
          message.error("Bad request - Cannot delete this purchase");
        } else {
          message.error(
            `Failed to delete purchase - Status: ${response.status}`
          );
        }
      }
    } catch (error) {
      if (isMounted.current) {
        if (error.response) {
          const status = error.response.status;
          const errorMessage =
            error.response.data?.message ||
            error.response.data?.error ||
            "Unknown error";

          if (status === 400) {
            message.error(`Bad request: ${errorMessage}`);
          } else if (status === 404) {
            message.error("Purchase not found");
          } else if (status === 403) {
            message.error("You don't have permission to delete this purchase");
          } else if (status === 500) {
            message.error("Server error - Please try again later");
          } else {
            message.error(`Failed to delete purchase: ${errorMessage}`);
          }
        } else {
          message.error("Network error - Please check your connection");
        }
      }
    }
  };

  const handleView = (item) => {
    history.push(`/auth/purchase/view/${item.id}`);
  };

  const handleEdit = (item) => {
    setSelectedPurchase(item);
    setOpenForm(true);
    fetchAccounts();
  };

  const handleFormToggle = () => {
    setOpenForm(!openForm);
    setSelectedPurchase(null);
  };

  const updateUrl = (page, take, search) => {
    const params = new URLSearchParams();
    if (page > 1) params.set("page", page);
    if (take !== 10) params.set("take", take);
    if (search) params.set("search", search);
    history.replace(params.toString() ? `${location.pathname}?${params}` : location.pathname);
  };

  const handleSearch = (value) => {
    const take = Number(new URLSearchParams(location.search).get("take")) || 10;
    updateUrl(1, take, value);
    window.scrollTo(0, 0);
  };

  const handleTableChange = (p, t) => {
    const search = new URLSearchParams(location.search).get("search") || "";
    updateUrl(p, t, search);
    window.scrollTo(0, 0);
  };

  return (
    <div>
      <PageHeader
        breadScrum="Admin / Purchase"
        RightButton="New / Purchase"
        RightClcik={handleFormToggle}
      />
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Input.Search
          placeholder="Search by box Number"
          value={searchValue}
          onSearch={handleSearch}
          onChange={(e) => setSearchValue(e.target.value)}
          allowClear
          onClear={() => {
            setSearchValue("");
            handleSearch("");
          }}
          style={{ width: 300 }}
        />
      </div>
      {loading ? (
        <LoadingBox />
      ) : (
        <div>
          <DataTable
            data={data}
            meta={meta}
            onDelete={handleDelete}
            onUpdate={handleEdit}
            onView={handleView}
            Onchange={handleTableChange}
            expandedRowKeys={expandedRowKeys}
            setExpandedRowKeys={setExpandedRowKeys}
          />
          {openForm && (
            <PurchaseForm
              data={data}
              accounts={accounts}
              visible={openForm}
              relaod={() => {
                const params = new URLSearchParams(location.search);
                loadData(Number(params.get("page")) || 1, Number(params.get("take")) || 10, params.get("search") || "");
              }}
              selectedPurchase={selectedPurchase}
              onClose={handleFormToggle}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Purchase;
